import { isEmpty } from 'lodash'
import { getAllCountries } from '../../../configs/supportedCountries'

const OPTIONAL_LABEL_PATTERN = /\(Optional\)/i
const NA_LABEL = 'N/A'

const hasOptionalIndicator = label => Boolean(label && OPTIONAL_LABEL_PATTERN.test(label))

const deriveFieldRequirements = country => {
  const labels = country?.labels || {}
  return {
    requiresCity: labels.city ? !hasOptionalIndicator(labels.city) : true,
    requiresPostcode: labels.postcode ? !hasOptionalIndicator(labels.postcode) : true,
    requiresRegion: labels.region ? labels.region !== NA_LABEL && !hasOptionalIndicator(labels.region) : true
  }
}

const COUNTRY_FIELD_REQUIREMENTS = (() => {
  try {
    return getAllCountries().reduce((acc, country) => {
      acc[country.country_code] = deriveFieldRequirements(country)
      return acc
    }, {})
  } catch (error) {
    return {}
  }
})()

const DEFAULT_FIELD_REQUIREMENTS = {
  requiresCity: true,
  requiresPostcode: true,
  requiresRegion: true
}

const getCountryRequirements = countryCode => {
  if (!countryCode) return DEFAULT_FIELD_REQUIREMENTS
  return COUNTRY_FIELD_REQUIREMENTS[countryCode] || DEFAULT_FIELD_REQUIREMENTS
}

const isStateValueValid = stateValue => {
  if (!stateValue || typeof stateValue !== 'string') return false
  const trimmed = stateValue.trim()
  if (!trimmed) return false
  return trimmed.toLowerCase() !== 'select state'
}

/**
 * Required fields for a valid billing address
 */
const REQUIRED_BILLING_FIELDS = ['full_name', 'address_line_1']

/**
 * Checks if billing address has all required fields filled
 * @param {Object} address - Billing address object
 * @returns {boolean}
 */
export const hasBillingAddressRequiredFields = address => {
  if (!address || isEmpty(address)) return false

  const baseFieldsValid = REQUIRED_BILLING_FIELDS.every(field => address[field] && address[field].trim() !== '')
  if (!baseFieldsValid) return false

  const { requiresCity, requiresPostcode, requiresRegion } = getCountryRequirements(address.country_code)

  if (requiresCity && !(address.city && address.city.trim() !== '')) return false
  if (requiresPostcode && !(address.zip_code && address.zip_code.trim() !== '')) return false
  if (requiresRegion && !isStateValueValid(address.state)) return false

  return true
}

/**
 * Validates billing address - returns true if valid for checkout
 * Note: billingAddress should always contain the address data (even when "same as shipping" is checked,
 * the shipping address is copied to billingAddress in Redux)
 * @param {Object} params
 * @param {number} params.youPay - Amount due in cents
 * @param {Object} params.billingAddress - Selected billing address
 * @param {boolean} params.allDigital - Whether all items are digital delivery
 * @param {boolean} params.requireBillingAddressForFreeOrders - Whether billing is required for free orders
 * @returns {boolean} - True if billing address is valid
 */
export const validateBillingAddress = ({
  youPay,
  billingAddress,
  allDigital = false,
  requireBillingAddressForFreeOrders = false
}) => {
  const isRequired = youPay > 0 || requireBillingAddressForFreeOrders

  // If not required, always valid
  if (!isRequired) return true

  // If required, check that address has all required fields
  return hasBillingAddressRequiredFields(billingAddress)
}

/**
 * Checks if customer/shipping address is valid
 * @param {Object} customerAddress - Selected customer address
 * @param {boolean} allDigital - Whether all items are digital delivery
 * @returns {boolean}
 */
export const isCustomerAddressValid = (customerAddress, allDigital) => {
  return !isEmpty(customerAddress) || allDigital
}

/**
 * Checks if payment method is valid for the given gateway
 * @param {Object} params
 * @param {string} params.paymentMethod - Payment method ('credit_card', 'net30')
 * @param {string} params.paymentGatewayType - Gateway type ('stripe', 'authorize_net')
 * @param {boolean} params.net30Confirmed - Whether net30 is confirmed
 * @param {number} params.youPay - Amount due in cents
 * @returns {boolean}
 */
export const isPaymentMethodValid = ({ paymentMethod, paymentGatewayType, net30Confirmed, youPay }) => {
  if (youPay === 0) return true

  switch (paymentMethod) {
    case 'credit_card':
      return paymentGatewayType === 'stripe' || paymentGatewayType === 'authorize_net'
    case 'net30':
      return net30Confirmed
    default:
      return false
  }
}

/**
 * Checks if there are any checkout errors in the errors object
 * @param {Object} errors - Checkout errors object
 * @returns {boolean}
 */
export const hasCheckoutErrors = errors => {
  return Object.values(errors).some(v => v === true || (typeof v === 'object' && !isEmpty(v)))
}

/**
 * Checks if payment is restricted based on customer group settings
 * @param {Object} params
 * @param {Object} params.customerGroup - Customer group settings
 * @param {number} params.youPay - Amount due in cents
 * @param {Object} params.creditsUsed - Credits used object
 * @param {boolean} params.net30Confirmed - Whether net30 is confirmed
 * @returns {boolean} - True if payment is restricted (checkout should be blocked)
 */
export const isPaymentRestricted = ({ customerGroup, youPay, creditsUsed, net30Confirmed }) => {
  if (youPay === 0) return false

  const hasCreditsApplied =
    creditsUsed.lmsPoints || creditsUsed.accountCredits || creditsUsed.giftCard || creditsUsed.coopCredits

  // Credit card hidden, no net30 allowed, no credits covering balance
  if (customerGroup.hide_credit_card && !customerGroup.net_30_payments && !hasCreditsApplied && !net30Confirmed) {
    return true
  }

  // Credit card hidden, net30 allowed but not confirmed
  if (customerGroup.hide_credit_card && customerGroup.net_30_payments && !net30Confirmed) {
    return true
  }

  return false
}

/**
 * Validates minimum credit card total (must be at least $0.50)
 * @param {number} youPay - Amount due in cents
 * @param {string} paymentMethod - Payment method
 * @returns {boolean} - True if validation fails (total is below minimum)
 */
export const validateMinimumCreditCardTotal = (youPay, paymentMethod) => {
  const MINIMUM_TOTAL_CENTS = 50
  return paymentMethod === 'credit_card' && youPay < MINIMUM_TOTAL_CENTS && youPay > 0
}

/**
 * Gets validation message for minimum credit card payment
 * @returns {{ title: string, message: string }}
 */
export const getMinimumCreditCardValidationMessage = () => ({
  title: 'Minimum Total Required for Credit Card Payments',
  message:
    'Your order total is below $0.50. To pay with a credit card, your order must be at least $0.50 after tax and shipping. Please add more items to your cart or choose another payment method.'
})

/**
 * Validates checkout form fields
 * @param {Array} checkoutForm - Array of form field objects
 * @returns {boolean}
 */
export const validateCheckoutForm = checkoutForm => {
  return checkoutForm.every(field => {
    switch (field.field_type) {
      case 'checkbox':
        return field.options.filter(option => option.is_required).every(option => option.checked)
      case 'radio':
        return !field.options[0].is_required || field.options.some(option => option.checked)
      case 'customer_notes':
        return !field.options[0].is_required || (field.options[0].value && field.options[0].value.length > 0)
      default:
        return true
    }
  })
}

/**
 * Comprehensive checkout validation - combines all validation checks
 * @param {Object} params
 * @param {Object} params.checkout - Checkout state
 * @param {Object} params.customerGroup - Customer group settings
 * @param {Object} params.errors - Checkout errors
 * @param {Object} params.reduxErrors - Legacy Redux errors
 * @param {number} params.youPay - Amount due in cents
 * @param {string} params.paymentGatewayType - Payment gateway type
 * @param {boolean} params.allDigital - Whether all items are digital
 * @param {boolean} params.checkoutFormIsValid - Whether custom form is valid
 * @param {boolean} params.checkoutHasNoErrors - Whether checkout has no errors
 * @param {boolean} params.requireBillingAddressForFreeOrders - Whether billing is required for free orders
 * @returns {{ canProceed: boolean, validationState: Object }}
 */
export const validateCheckout = ({
  checkout,
  customerGroup,
  errors,
  reduxErrors,
  youPay,
  paymentGatewayType,
  allDigital,
  checkoutFormIsValid,
  checkoutHasNoErrors,
  requireBillingAddressForFreeOrders = false
}) => {
  const billingValid = validateBillingAddress({
    youPay,
    billingAddress: checkout.selectedBillingAddress,
    allDigital,
    requireBillingAddressForFreeOrders
  })

  const customerAddressValid = isCustomerAddressValid(checkout.selectedCustomerAddress, allDigital)

  const paymentValid = isPaymentMethodValid({
    paymentMethod: checkout.paymentMethod,
    paymentGatewayType,
    net30Confirmed: checkout.net30Confirmed,
    youPay
  })

  const checkoutErrorsExist = hasCheckoutErrors(errors)
  const legacyErrorsExist = Boolean(reduxErrors?.shipping_errors)

  const paymentRestricted = isPaymentRestricted({
    customerGroup,
    youPay,
    creditsUsed: checkout.creditsUsed,
    net30Confirmed: checkout.net30Confirmed
  })

  const canProceed =
    customerAddressValid &&
    billingValid &&
    paymentValid &&
    !checkoutErrorsExist &&
    !legacyErrorsExist &&
    checkoutFormIsValid &&
    checkoutHasNoErrors &&
    !paymentRestricted

  return {
    canProceed,
    validationState: {
      billingValid,
      customerAddressValid,
      paymentValid,
      checkoutErrorsExist,
      legacyErrorsExist,
      paymentRestricted,
      checkoutFormIsValid,
      checkoutHasNoErrors
    }
  }
}
